import ClientForm from "@components/ClientForm";
import CustomSnackBar from "@components/CustomSnackBar";
import {
  INITIAL_SNACKBAR_STATE,
  ISnackbarAlert,
  setErrorAlert,
  setInitialSnackbarState
} from "@constants/alerts";
import ROUTES from "@constants/routing";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {
  Box,
  Button,
  LinearProgress,
  Paper,
  SelectChangeEvent,
  Typography,
  useTheme
} from "@mui/material";
import { usePatchClientLogo, useSaveClient } from "@services/api";
import {
  ClientCreate,
  ClientUIMMMModelMetric,
  ClientUIModule
} from "@services/api/types";
import { useQueryClient } from "@tanstack/react-query";
import NavigationButtons from "@views/ViewAdminPanel/components/NavigationButtons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import "react-phone-input-2/lib/material.css";
import { useNavigate } from "react-router-dom";
type T_CLIENT_STATUS = "draft" | "saving" | "created" | "failed";

const ViewNewClient = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const DEFAULT_BASE_COLOR = theme.palette.primary.main;
  const [client, setClient] = useState<ClientCreate>({
    name: "",
    email: "",
    phone: "",
    label: "",
    logo_path: "",
    base_color: DEFAULT_BASE_COLOR,
    kpi_label: "",
    currency: "",
    ui_modules: [],
    mmm_model_config: {
      metrics: []
    }
  });
  const [clientStatus, setClientStatus] = useState<T_CLIENT_STATUS>("draft");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [snackbarAlert, setSnackbarAlert] = useState<ISnackbarAlert>({
    ...INITIAL_SNACKBAR_STATE
  });
  const [clientSaving, setClientSaving] = useState(false);

  const mutationSaveClient = useSaveClient();
  const mutationPatchClientLogo = usePatchClientLogo();
  const queryClient = useQueryClient();

  const onGoBack = () => {
    navigate(ROUTES.ADMIN_PANEL);
  };

  const onContinue = () => {
    setClientStatus("draft");
    navigate(ROUTES.ADMIN_PANEL);
  };

  const handleFieldChange = (field: keyof ClientCreate, value: any) => {
    const value_fmt = `${value}`.length > 0 ? value : "";
    const updated = { ...client, [field]: value_fmt };
    setClient(updated);
  };

  const handleMMMModelMetricsChange = (event: SelectChangeEvent) => {
    const {
      target: { value }
    } = event;

    const selectedMetrics =
      typeof value === "string"
        ? (value.split(",") as ClientUIMMMModelMetric[])
        : (value as ClientUIMMMModelMetric[]);
    const updated = {
      ...client,
      mmm_model_config: { ...client.mmm_model_config, metrics: selectedMetrics }
    };
    setClient(updated);
  };

  const handleMMMModelUIModulesChange = (event: SelectChangeEvent) => {
    const {
      target: { value }
    } = event;
    const selectedUIModules =
      typeof value === "string"
        ? (value.split(",") as ClientUIModule[])
        : (value as ClientUIModule[]);
    const updated = { ...client, ui_modules: selectedUIModules };
    setClient(updated);
  };

  const onSave = (e: any) => {
    e.preventDefault();
    if (!e.target.checkValidity() || selectedFile === null) {
      setSnackbarAlert(setErrorAlert(t("newClient.validation.formInvalid")));
    } else {
      client.logo_path = "";
      setClientStatus("saving");
      setClientSaving(true);
      mutationSaveClient.mutate(client, {
        onSuccess: (savedClient) => {
          mutationPatchClientLogo.mutate(
            {
              client_id: savedClient.id.toString(),
              logo: selectedFile
            },
            {
              onSuccess: () => {
                setClientStatus("created");
                setClientSaving(false);
                queryClient.invalidateQueries({
                  queryKey: ["clients"]
                });
                queryClient.refetchQueries({
                  queryKey: ["clients"]
                });
              },
              onError: () => {
                setClientStatus("failed");
                setClientSaving(false);
                setSnackbarAlert(
                  setErrorAlert(t("newClient.validation.errorSavingLogo"))
                );
              }
            }
          );
        },
        onError: () => {
          setClientStatus("failed");
          setClientSaving(false);
          setSnackbarAlert(
            setErrorAlert(t("newClient.validation.errorSaving"))
          );
        }
      });
    }
  };

  return (
    <Box className="w-full p-4">
      <NavigationButtons disabled={clientStatus === "saving"} />
      <Box className="flex justify-center w-full p-2 md:p-8 mt-8">
        <Paper className="w-full">
          <Box className="flex items-center justify-center pt-16 gap-2">
            <AddBusinessIcon color="success" sx={{ fontSize: "2.0rem" }} />
            <Typography variant="h6">{t("common.newClient")}</Typography>
          </Box>
          {clientStatus === "draft" && (
            <ClientForm
              mode="create"
              client={client}
              selectedFile={selectedFile}
              setSelectedFile={setSelectedFile}
              onSave={onSave}
              onGoBack={onGoBack}
              processing={clientSaving}
              handleFieldChange={handleFieldChange}
              handleMMMModelMetricsChange={handleMMMModelMetricsChange}
              handleMMMModelUIModulesChange={handleMMMModelUIModulesChange}
            />
          )}
          {clientStatus === "saving" && (
            <Box className="w-full p-8">
              <Typography variant="h5" color="success" className="pb-4">
                {t("createClientProgressDialog.status.clientSaving")}
              </Typography>
              <LinearProgress color="success" sx={{ height: "1.0rem" }} />
            </Box>
          )}
          {clientStatus === "created" && (
            <Box className="w-full p-8">
              <Typography variant="h5" color="success" className="pb-4">
                {t("createClientProgressDialog.status.clientCreated")}
              </Typography>

              <Button color="success" variant="contained" onClick={onContinue}>
                {t("common.continue")}
                <ArrowForwardIosIcon sx={{ fontSize: "1.5rem" }} />
              </Button>
            </Box>
          )}
          {clientStatus === "failed" && (
            <Box className="w-full p-8">
              <Typography variant="h5" color="error">
                {t("createClientProgressDialog.status.clientCreatedFailed")}
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
      <CustomSnackBar
        id={snackbarAlert.message}
        key={snackbarAlert.message}
        open={snackbarAlert.open}
        message={snackbarAlert.message}
        handleClose={() => setSnackbarAlert(setInitialSnackbarState())}
        autoHideDuration={snackbarAlert.autoHideDuration}
        color={snackbarAlert.severity}
      />
    </Box>
  );
};

export default ViewNewClient;
